import type { Theme } from "../..";

const getPlainPrimitives = (themePrimitives: Theme.Primitives) => {
	const allPrimitives: { [key: string]: string } = {};

	const getPrimitives = (primitives: Array<Theme.Primitive>) => {
		for (const primitive of primitives) {
			for (const { cssVar, value } of primitive.values) {
				allPrimitives[cssVar] = value;
			}
		}
	};

	if (Array.isArray(themePrimitives)) {
		getPrimitives(themePrimitives);
	} else {
		for (const primitives of Object.values(themePrimitives)) {
			getPrimitives(primitives);
		}
	}

	return allPrimitives;
};

const plainThemePrimitives = (theme: Theme.GlobalTheme | Theme.Theme) => {
	let primitives: { [key: string]: string } = {
		...getPlainPrimitives(theme.primitives),
	};

	if ("subthemes" in theme && theme.subthemes) {
		for (const subtheme of theme.subthemes) {
			primitives = {
				...primitives,
				...getPlainPrimitives(subtheme.primitives),
			};
		}
	}

	return primitives;
};

const filterColorPrimitives = (
	primitives: Array<Theme.Primitive>,
	allPrimitives: { [key: string]: string },
) => {
	return primitives
		.map((primitive) => {
			return {
				...primitive,
				// replaces var(...) with variable value, so that CSS.supports('color', value)
				// does not trigger a false positive
				values: primitive.values
					.map((primitiveValue) => {
						const regexp = /var\((.*?)\)/g;
						const matches = [...primitiveValue.value.matchAll(regexp)];

						for (const match of matches) {
							if (match.length >= 2) {
								primitiveValue.value = primitiveValue.value.replace(
									match[0],
									allPrimitives[match[1]],
								);
							}
						}

						return { ...primitiveValue };
					})
					.filter(({ value }) => CSS.supports("color", value)),
			};
		})
		.filter((primitive) => primitive.values.length);
};

const getColorPrimitives = (
	theme: Theme.GlobalTheme | Theme.Theme,
): Theme.Primitives => {
	const primitives: { [key: string]: string } = plainThemePrimitives(theme);

	if (Array.isArray(theme.primitives)) {
		return filterColorPrimitives(theme.primitives, primitives);
	}
	const { common, light, dark } = theme.primitives;

	return {
		common: common && filterColorPrimitives(common, primitives),
		light: filterColorPrimitives(light, primitives),
		dark: filterColorPrimitives(dark, primitives),
	};
};

const filterPrimitivesByType = (
	primitives: Array<Theme.Primitive>,
	type: string | Array<string>,
) => {
	return primitives.filter((primitive) =>
		Array.isArray(type) ? type.includes(primitive.id) : primitive.id === type,
	);
};

const getPrimitivesByType = (
	theme: Theme.GlobalTheme | Theme.Theme,
	type: string | Array<string>,
): Theme.Primitives => {
	if (Array.isArray(theme.primitives)) {
		return filterPrimitivesByType(theme.primitives, type);
	}
	const { common, light, dark } = theme.primitives;

	return {
		common: common && filterPrimitivesByType(common, type),
		light: filterPrimitivesByType(light, type),
		dark: filterPrimitivesByType(dark, type),
	};
};

const hasPrimitives = (theme: Theme.Theme) => {
	if (Array.isArray(theme.primitives)) {
		return theme.primitives.length;
	}
	const { common, light, dark } = theme.primitives;

	return common?.length || light.length || dark.length;
};

const filterThemePrimitives = (
	themes: Array<Theme.GlobalTheme | Theme.Theme>,
	selectedSubtheme?: string,
	type?: string | Array<string>,
) => {
	const color = typeof type === "undefined";

	return themes
		.map((theme) => {
			return {
				...theme,
				primitives: color
					? getColorPrimitives(theme)
					: getPrimitivesByType(theme, type),
				subthemes:
					("subthemes" in theme &&
						theme.subthemes &&
						theme.subthemes
							.map((subtheme) => ({
								...subtheme,
								primitives: color
									? getColorPrimitives(subtheme)
									: getPrimitivesByType(subtheme, type),
							}))
							.filter(
								(subtheme) =>
									hasPrimitives(subtheme as Theme.Theme) &&
									(selectedSubtheme ? subtheme.id === selectedSubtheme : true),
							)) ||
					[],
			};
		})
		.filter(
			(theme) => hasPrimitives(theme as Theme.Theme) || theme.subthemes.length,
		);
};

export const filterThemePrimitivesByType = (
	themes: Array<Theme.GlobalTheme | Theme.Theme>,
	primitives: string | Array<string>,
	subtheme?: string,
) => {
	return filterThemePrimitives(themes, subtheme, primitives);
};

export const filterThemePrimitivesByColor = (
	themes: Array<Theme.GlobalTheme | Theme.Theme>,
	subtheme?: string,
) => {
	return filterThemePrimitives(themes, subtheme);
};
